/* eslint-disable react/no-multi-comp */
import React from "react";
import { connect } from "react-redux";

// This is a React Router v6 app
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AdminDashboard } from "./components/admin";
import { AvailabilitiesRedirect } from "./components/availabilities/AvailabilitiesDashboard";
import CalendarManager from "./components/calendar/Calendar";
import Header from "./components/common/Header";
import { CenteredLoader } from "./components/common/loader";
import CompetitionEditor from "./components/competitions/CompetitionEditor";
import { NotAuthorized, NotFound404 } from "./components/error/HttpPages";
import ServerError from "./components/error/ServerError";
import HomePage from "./components/home/HomePage";
import PrivacyPolicyPage from "./components/internal/PrivacyPolicy";
import LoginPage from "./components/login/LoginPage";
import LogoutPage from "./components/login/LogoutPage";
import RosterManager from "./components/management/roster/RosterManager";
import PersonalProfilePage from "./components/profile/PersonalProfilePage";
import ProfilePage from "./components/profile/ProfilePage";
import ResetPassword from "./components/resetPassword/ResetPassword";
import PublishedRoster from "./components/roster/publish/PublishedRoster";
import PersonalSettingsPage from "./components/settings/PersonalSettings";
import TeamManager from "./components/teams/TeamManager";
import { logout, notAuthenticated } from "./data/actions/authenticationActions";
import { IState } from "./data/reducers/state";
import { Role } from "./types/persons/Role";
import { IPerson } from "./types/persons/person";
import { Privilege } from "./types/persons/privilege";

interface IRoutesProps {
  isLoading: boolean;
  hasAuthenticated: boolean;
  error: null | { error: string | null; code: string | null };

  role: Role;
  privilege: Privilege;
  self: IPerson | null;
  logout: () => Promise<any>;
  navigateTo: (to: string) => void;
}

const resetPasswordRoute = (
  <Route path="/resetpassword" element={<ResetPassword />} />
);
const privacyPolicyRoutes = (
  <Route path="/privacy" element={<PrivacyPolicyPage />} />
);

function AppointmentRoutes(props) {
  const priv = props.privilege;
  const { role } = props;
  const canManage = Privilege.canManage(priv);
  return (
    <Routes>
      {/* Appointments admin route can be added here if needed */}
    </Routes>
  );
}

function AvailsRedirect(props) {
  if (props.self?.externalIdentifier) {
    return (
      <Navigate to={`/u/${props.self?.externalIdentifier}/availabilities`} />
    );
  }
  return (
    <AvailabilitiesRedirect
      externalIdentifier={props.self?.externalIdentifier}
    />
  );
}

const RegularRoutes: React.FunctionComponent<IRoutesProps> = (props) => (
  <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/admin/*" element={<AdminRoutes {...props} />} />
    <Route path="/roster/:shiftId?/:sessionId?" element={<RosterManager />} />
    <Route
      path="/shift/published/:sessionToken/"
      element={<PublishedRoster />}
    />
    <Route path="/availabilities" element={<AvailsRedirect {...props} />} />
    <Route
      path="/competitions/:competitionId/"
      element={<CompetitionEditor />}
    />
    <Route path="/teams/:teamId" element={<TeamManager />} />
    <Route path="/calendar" element={<CalendarManager />} />
    {props.self && props.self.externalIdentifier && (
      <Route
        path={`/u/${props.self.externalIdentifier}/:section?`}
        element={<PersonalProfilePage />}
      />
    )}
    {props.self && props.self.externalIdentifier && (
      <Route path="/settings/:submenu?" element={<PersonalSettingsPage />} />
    )}
    <Route path="/me" element={<PersonalProfilePage />} />
    <Route path="/u/:id/:section?" element={<ProfilePage />} />

    <Route
      {...props}
      path="/appointments/*"
      element={<AppointmentRoutes {...props} />}
    />

    <Route path="/login" element={<Navigate to="/" />} />
    <Route path="/logout" element={<LogoutPage />} />
    {resetPasswordRoute}
    {privacyPolicyRoutes}
    <Route errorElement={<NotFound404 />} />
    <Route path="*" element={<NotFound404 />} />
  </Routes>
);
function AdminRoutes(props: IRoutesProps) {
  const priv = props.privilege;
  const { role, self } = props;
  const isManager = Privilege.hasPrivilege(priv, Privilege.manager);
  const isAdmin = self?.admin || false;
  const isSupervisor = Role.hasRole(role, Role.supervisor);
  return (
    <Routes>
      {/* Admin Dashboard with direct component rendering */}
      {(isManager || isAdmin || isSupervisor) && <>
        <Route path="/shifts/:shiftId" element={<AdminDashboard />} />
        <Route path="/*" element={<AdminDashboard />} />
      </>}
      <Route path="*" element={<NotAuthorized />} />
    </Routes>
  );
}

const unauthenticatedRoutes = (props, withLoginRedirect) => {
  const location = useLocation();

  const routes = [
    resetPasswordRoute,
    privacyPolicyRoutes,
    <Route path="/login" element={<LoginPage />} />,
    <Route
      path="/shift/published/:sessionToken/"
      element={<PublishedRoster />}
    />,
  ];

  if (withLoginRedirect) {
    routes.push(
      <Route
        path="*"
        element={
          <Navigate
            to="/login"
            state={{ previousLocation: location.pathname }}
          />
        }
      />,
    );
  }
  return routes;
};

const loadingRoutes = (props) => (
  <Routes>
    {resetPasswordRoute}
    {...unauthenticatedRoutes(props, false)}
    <Route element={<CenteredLoader />} />
  </Routes>
);

const SSRoutes: React.FunctionComponent<IRoutesProps> = (
  props: IRoutesProps,
) => {
  const { self, isLoading, hasAuthenticated, error, logout, role } = props;

  if (error && error.code != "401")
    return <ServerError error={error.error} code={error.code} />;
  if (isLoading === true) return loadingRoutes(props);
  if (!hasAuthenticated)
    return <Routes>{...unauthenticatedRoutes(props, true)}</Routes>;

  if (!self) return <ServerError error={error?.error} />;

  return (
    <>
      <Header person={self} logout={logout} role={role} privilege={props.privilege} />
      <main>
        <RegularRoutes {...props} />
        {/* <ToastContainer /> */}

      </main>
    </>
  );
};

function mapStateToProps(state: IState, props) {
  return {
    isLoading: state.auth.loading,
    hasAuthenticated: state.auth.hasAuthenticated,
    error: state.auth.error,

    privilege: state.auth.privilege,

    role: state.auth.role,
    self: state.persons.self && state.persons.map[state.persons.self],
  };
}

function mapDispatchToProps(dispatch) {
  return {
    notAuthenticated: () => dispatch(notAuthenticated()),
    logout: () => dispatch(logout()),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SSRoutes);
